import { useDispatch, useSelector } from "react-redux";

import {
  requestEContractShareConfirm,
  requestEContractShareLink,
} from "actions";

import { EContractParty } from "constants/eContract";

import { buildEContractSmsHref } from "lib/econtracts";
import toast from "lib/toast";

import { getIsOnline, getIsStaff } from "selectors";

import { useIsMobile } from "hooks";

export function useEContractShare(eContractId?: string) {
  const dispatch = useDispatch();
  const isOnline = useSelector(getIsOnline);
  const isStaff = useSelector(getIsStaff);
  const isMobile = useIsMobile();

  // TODO: remove the isStaff gate once share is ready for a full release
  const canShare = isStaff && isOnline && isMobile;

  const confirmShare = (party: EContractParty) => {
    dispatch(requestEContractShareConfirm({ eContractId, party }));
  };

  const shareLink = async (link: string, party: EContractParty) => {
    if ("share" in navigator) {
      try {
        await navigator.share({ url: link });
        // Only counts as shared once the OS share sheet resolves - a
        // cancelled share sheet rejects with AbortError and isn't counted.
        confirmShare(party);
      } catch (error) {
        const errorName = (error as Error)?.name;
        if (errorName === "AbortError") {
          // User cancelled the share sheet - not an error.
        } else if (errorName === "NotAllowedError") {
          // The share link request took long enough that the browser's user-
          // gesture window for navigator.share() lapsed - not a real failure.
          toast.error("Could not open the share sheet, please try again.");
        } else {
          toast.error("Could not share eContract.");
        }
      }
      return;
    }

    // No Web Share support - hand off to the device's SMS app instead.
    // There's no completion signal for this path, so the handoff itself
    // is counted as the share.
    window.location.href = buildEContractSmsHref(link);
    confirmShare(party);
  };

  const shareEContract = (party: EContractParty) => {
    dispatch(
      requestEContractShareLink(
        { eContractId, party },
        {
          onSuccess: ({ link }: { link: string }) => {
            shareLink(link, party);
          },
        },
      ),
    );
  };

  return { shareEContract, canShare };
}
